import React, { useState, useEffect } from 'react';
import {
    ChevronRight,
    ChevronDown,
    ChevronLeft,
    Loader2,
    AlertCircle,
    RefreshCw
} from 'lucide-react';
import apiClient from '../utils/apiClient';
import { getSelectedSport } from '../utils/helpers';
import { Pagination } from './design-system';

const RecuritingPortal = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [playersData, setPlayersData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    // Mock functions - replace with your actual implementations
    const fetchPlayers = async (page = currentPage, size = pageSize) => {
        try {
            setLoading(true);
            setError(null);

            const response = await apiClient.get(
                `${getSelectedSport()}/recruiting-players/?page=${page}&page_size=${size}&schema=${getSelectedSport()}`
            );

            const data = response?.data;
            console.log('data', data)
            setPlayersData(data?.data || []);
            setTotalRecords(parseInt(data?.Total_Results) || 0);
            setTotalPages(parseInt(data?.Total_Pages) || 0);
            setCurrentPage(parseInt(data?.Page) || page);

        } catch (err) {
            setError('Failed to fetch player data. Please try again.');
            console.error('API Error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await fetchPlayers(currentPage, pageSize);
        } finally {
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchPlayers();
    }, []);

    const getScoreColor = (rating) => {
        if (rating >= 80) return 'bg-green-900/40 text-green-400';
        if (rating >= 75) return 'bg-[#E8B800]/20 text-[#E8B800]';
        if (rating >= 70) return 'bg-orange-900/40 text-orange-400';
        return 'bg-red-900/40 text-red-400';
    };

    const toggleExpanded = (playerId) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(playerId)) {
            newExpanded.delete(playerId);
        } else {
            newExpanded.add(playerId);
        }
        setExpandedRows(newExpanded);
    };

    const handlePageChange = (page, size) => {
        setCurrentPage(page);
        setPageSize(size);
        fetchPlayers(page, size)
    };

    const LoadingState = () => (
        <div className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 border-t-4 border-b-4 border-[#E8B800] rounded-full animate-spin mb-4"></div>
            <p className="text-lg text-[#999999]">
                Running multi-agent evaluation system...
            </p>
            <p className="text-sm text-[#555555] mt-2">
                Analyzing data
            </p>
        </div>
    );

    const renderPagination = () => (
        <div className="mt-5 flex justify-center pb-5">
            <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={totalRecords}
                onChange={handlePageChange}
                showSizeChanger
                pageSizeOptions={['5', '10', '20', '50']}
                isDarkMode={true}
            />
        </div>
    );

    if (error) {
        return (
            <div className="p-6 min-h-screen bg-[#0A0A0A]">
                <div className="rounded-[14px] shadow-sm p-8 bg-[#111111]">
                    <div className="flex items-center justify-center space-x-2 text-red-400">
                        <AlertCircle size={24} />
                        <div>
                            <h3 className="text-lg font-semibold text-red-400">Error Loading Data</h3>
                            <p className="text-sm mt-1 text-[#555555]">{error}</p>
                            <button
                                onClick={() => fetchPlayers()}
                                className="mt-4 px-4 py-2 bg-[#E8B800] text-black rounded hover:bg-[#E8B800]/80"
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-2 min-h-screen bg-[#0A0A0A]">
            {/* New Header Section */}
            <div className="rounded-[14px] shadow-sm mb-4 bg-[#111111]">
                <div className="p-6 flex justify-between items-center border-[#1A1A1A]">
                    <div>
                        <h1 className="text-2xl font-bold text-[#F5F5F5]">
                            PreCollege Players
                        </h1>
                        <p className="text-sm text-[#555555] mt-1">
                            Live data from PreCollege Players portal
                        </p>
                    </div>
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={() => fetchPlayers()}
                            disabled={refreshing || loading}
                            className="flex items-center space-x-2 px-4 py-2 bg-[#E8B800] text-black rounded-lg hover:bg-[#E8B800]/80 focus:outline-none focus:ring-2 focus:ring-[#E8B800] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
                            <span>Refresh</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="rounded-[14px] shadow-sm bg-[#111111]">
                {/* Player Count Header */}
                <div className="p-6 border-b border-[#1A1A1A]">
                    <h3 className="text-lg font-semibold text-[#999999]">
                        {totalRecords} players found
                    </h3>
                </div>

                {loading && (
                    <div className="flex items-center justify-center p-12">
                        <LoadingState className="animate-spin text-[#E8B800]" size={32} />
                    </div>
                )}

                {!loading && (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="border-b bg-[#111111] border-[#1A1A1A]">
                                    <tr>
                                        <th className="text-left p-4 font-medium text-[#555555] uppercase text-xs tracking-wider w-8"></th>
                                        <th className="text-left p-4 font-medium uppercase text-xs tracking-wider min-w-60 text-[#555555]">PLAYER</th>
                                        <th className="text-left p-4 font-medium uppercase text-xs tracking-wider w-24 text-[#555555]">POSITION</th>
                                        <th className="text-left p-4 font-medium uppercase text-xs tracking-wider w-20 text-[#555555]">HEIGHT</th>
                                        <th className="text-left p-4 font-medium uppercase text-xs tracking-wider w-20 text-[#555555]">WEIGHT</th>
                                        <th className="text-left p-4 font-medium uppercase text-xs tracking-wider min-w-40 text-[#555555]">HIGH SCHOOL</th>
                                        <th className="text-left p-4 font-medium uppercase text-xs tracking-wider min-w-32 text-[#555555]">CITY</th>
                                        <th className="text-left p-4 font-medium uppercase text-xs tracking-wider w-32 text-[#555555]">EVALUATION DATE</th>
                                        <th className="text-left p-4 font-medium uppercase text-xs tracking-wider w-32 text-[#555555]">
                                            <span className="text-[#E8B800]">RATING</span> <ChevronDown size={12} className="inline ml-1" />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {playersData.map((player, index) => (
                                        <React.Fragment key={player.PlayerID}>
                                            <tr
                                                className="border-b cursor-pointer border-[#1A1A1A] hover:bg-[rgba(255,255,255,0.04)]"
                                                onClick={() => toggleExpanded(player.PlayerID)}
                                            >
                                                <td className="p-4">
                                                    <button
                                                        className="p-1 text-[#555555] hover:text-[#999999]"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleExpanded(player.PlayerID);
                                                        }}
                                                    >
                                                        {expandedRows.has(player.PlayerID) ?
                                                            <ChevronDown size={16} /> :
                                                            <ChevronRight size={16} />
                                                        }
                                                    </button>
                                                </td>
                                                <td className="p-4">
                                                    <div className="flex items-center space-x-3">
                                                        <div className="w-8 h-8 rounded flex items-center justify-center text-lg bg-[#1A1A1A]">
                                                            🏀
                                                        </div>
                                                        <div>
                                                            <div className="font-semibold text-[#F5F5F5]">{player.PlayerName}</div>
                                                            <div className="text-xs text-green-500 font-medium">Evaluated</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-4 font-medium text-[#999999]">{player.Position}</td>
                                                <td className="p-4 text-[#999999]">{player.Height}</td>
                                                <td className="p-4 text-[#999999]">{player.Weight} lbs</td>
                                                <td className="p-4 text-[#999999]">{player.HighSchool}</td>
                                                <td className="p-4 text-[#999999]">{player.City}</td>
                                                <td className="p-4 text-[#999999]">{player.ArticleDate}</td>
                                                <td className="p-4">
                                                    <span className={`px-2 py-1 text-sm font-semibold rounded ${getScoreColor(player.Rating)}`}>
                                                        {player.Rating}
                                                    </span>
                                                </td>
                                            </tr>
                                            {expandedRows.has(player.PlayerID) && (
                                                <tr>
                                                    <td colSpan="9" className="p-0">
                                                        <div className="border-t p-6 bg-[#111111] border-[#1A1A1A]">
                                                            <div className="rounded-[14px] border p-6 bg-[#0A0A0A] border-[#1A1A1A]">
                                                                <div className="flex items-center space-x-2 mb-4">
                                                                    <h3 className="font-semibold text-[#F5F5F5]">{player.ArticleTitle}</h3>
                                                                </div>
                                                                <div className="p-4 rounded-lg border bg-[#111111] border-[#1A1A1A]">
                                                                    <p className="leading-relaxed text-sm text-[#999999]">
                                                                        {player.ArticleText}
                                                                    </p>
                                                                </div>
                                                                {player.ArticleAuthor && player.ArticleDate && (
                                                                    <div className="flex justify-between items-center mt-4 pt-4 border-t text-xs border-[#1A1A1A] text-[#555555]">
                                                                        <span>Author: {player.ArticleAuthor}</span>
                                                                        <span>Date: {player.ArticleDate}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {renderPagination()}
                    </>
                )}
            </div>
        </div>
    );
};

export default RecuritingPortal;